import React, { useContext, useEffect, useState } from 'react';
import { EllipsisVerticalIcon, EyeIcon, PencilIcon } from '@heroicons/react/16/solid';
import { Button } from '../../components/Button';
import { Heading } from '../../components/Heading';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination'; // Import the Pagination component
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../components/Dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/Table';
import dateFormat from 'dateformat';
import { UserContext } from '../../context/userContext';
import { useAppSalesOrders } from '../../hooks/useAppSalesOrder';
import { Card } from '../../components/Card';
import { ListToolbarRow, ListToolbarSearch, SortMenu, ColumnsMenu, isColumnVisible } from '../../components/ListToolbar';

const COLUMNS = [
    { key: 'customer', label: 'Customer' },
    { key: 'product', label: 'Product' },
    { key: 'price', label: 'Price' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'created', label: 'Created on' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const AppSalesOrdersPage: React.FC = () => {
    const { loading, error, salesOrders, total, fetchSalesOrdersPaginated } = useAppSalesOrders();
    const [searchQuery, setSearchQuery] = useState<string>(''); // State to store the search query
    const [currentPage, setCurrentPage] = useState<number>(1);
    const perPage = 10;
    const navigate = useNavigate();
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));

    useEffect(() => {
        document.title = "Sales Orders";
    }, []);

    useEffect(() => {
        fetchSalesOrdersPaginated(currentPage, perPage, searchQuery || undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchQuery]);

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Sales Orders</Heading>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search sales orders…" />}>
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    {isColumnVisible(columnVisibility, 'customer') && <TableHeader>Customer</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'product') && <TableHeader>Product</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'price') && <TableHeader>Price</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'quantity') && <TableHeader>Quantity</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'created') && <TableHeader>Created on</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(salesOrders.length > 0) ? [...salesOrders].sort((a, b) => {
                                    const diff = new Date(a.created_time || 0).getTime() - new Date(b.created_time || 0).getTime();
                                    return sortOrder === 'newest' ? -diff : diff;
                                }).map((order, index) => (
                                    <TableRow key={order.id} title={`${order.product_description}`}>
                                        <TableCell>{(currentPage - 1) * perPage + index + 1}</TableCell>
                                        {isColumnVisible(columnVisibility, 'customer') && <TableCell>{order.customer_fullname}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'product') && <TableCell className="text-zinc-500">{order.product_description}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'price') && <TableCell className="text-zinc-500">{order.price}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'quantity') && <TableCell className="text-zinc-500">{order.quantity}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'created') && <TableCell className="text-zinc-500">{dateFormat(order.created_time?.toString(), "mmm dd, yyyy")}</TableCell>}
                                        <TableCell className="py-5 flex gap-4 text-zinc-500 justify-center">

                                            <a onClick={() => navigate(`/gp/salesorders/view/${order.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>

                                        </TableCell>
                                    </TableRow>
                                )) : <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">No sales orders found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(total / perPage)}
                            onPageChange={setCurrentPage}
                            totalResult={total}
                            dataName={'Sales Orders'}
                        />
                    </div>
                )}
            </ul>
            </Card>
        </>
    );
};

export default AppSalesOrdersPage;
